<template>
  <div class="date-request-product-list">
    <ProductsLoader
      v-show="loading"
      class="mt-4 mb-4"
      :is-horizontal="false"
      :always-wrap="true"
    />

    <div v-show="!loading" class="date-request-product-list__container">
      <transition-group name="fade-slide-up">
        <template
          v-for="item in products"
          :key="`date-request-product-card-${item.object}`"
        >
          <div class="date-request-product-list__item">
            <!-- Only show the ProductCard if this item is not selected -->
            <transition name="fade-slide-up">
              <ProductCard
                v-if="selectedEventId !== item.id"
                :disable-wishlist="true"
                v-bind="productCardProps(item)"
                :image-auto-height="true"
                @click="handleClick(item)"
              >
                <template #before-title>
                  <div v-if="requested" class="mb-1">
                    <Badge class="text-xs bg-success-light text-teal-900">
                      {{ t('dateRequest.eventSuggestions.requestSentShort') }}
                    </Badge>
                  </div>
                </template>
                <!-- Date request specific content slot -->
                <template #content-below-title>
                  <DateRequestEventInfo
                    :private="privateRequest"
                    :hide-button="requested"
                    :item="item"
                    :pricing-data="getPricingForItem(item.id)"
                    :loading-pricing="pricingsLoading"
                    @preview-event="handleClick"
                  />
                </template>
              </ProductCard>
            </transition>

            <!-- Only show the preview if this item is selected -->
            <transition name="fade-slide-up">
              <LazyDateRequestEventPreview
                v-if="selectedEventId === item.id"
                :event-data="item"
                :private-request="privateRequest"
                :pricing-data="getPricingForItem(item.id)"
                :selected-ticket-quantity="quantity"
                @request-success="handleRequestSuccess"
              />
            </transition>
          </div>
        </template>
      </transition-group>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { DateRequestSimilarEvent } from '~/composables/types/event'
import type { ProductCardImageBadge } from '~/components/product/ProductCardImage.vue'
import ProductsLoader from '~/components/product/ProductsLoader.vue'
import { useDateRequestPricings } from '~/composables/useDateRequestPricings'

const { t } = useI18n()

interface Props {
  products: DateRequestSimilarEvent[]
  loading?: boolean
  requested?: boolean
  privateRequest?: boolean
}

const {
  products,
  loading = false,
  requested = false,
  privateRequest = false,
} = defineProps<Props>()

const { savedFormData } = useDateRequestFormStorage()

const scrollDateRequestDrawerToTop = inject<() => void>(
  'scrollDateRequestDrawerToTop',
  () => {}
)

// Setup pricing fetching for all items
const productsRef = computed(() => products)
const quantity = computed(() => savedFormData.value?.numberOfTickets ?? 1)
const isPrivate = computed(() => privateRequest)

const {
  loading: pricingsLoading,
  fetchAllPricings,
  getPricingForItem,
} = useDateRequestPricings(productsRef, {
  quantity,
  isPrivate,
})

// Fetch pricings when products change
watch(
  [productsRef, quantity, isPrivate],
  async () => {
    if (products.length > 0 && !requested) {
      await fetchAllPricings()
    }
  },
  { immediate: true }
)

const emit = defineEmits<{
  requestSuccess: [eventData: DateRequestSimilarEvent]
}>()

const handleRequestSuccess = (eventData: DateRequestSimilarEvent) => {
  emit('requestSuccess', eventData)
}

// State to track which event is currently selected/opened
const selectedEventId = ref<string | null>(null)

const getBadges = (item: DateRequestSimilarEvent): ProductCardImageBadge[] => {
  const badges: ProductCardImageBadge[] = []

  if (item.has_promotions)
    badges.push({ label: t('product.card.sale'), variant: 'promo' as const })

  return badges
}

// Product card props optimized for date request context
const productCardProps = (item: DateRequestSimilarEvent) => {
  return {
    // Essential props for ProductCard display
    image: replaceSubdomainToProd(item.media?.data?.thumb || ''),
    title: item.title || 'date-request-event',
    alt: item.title || 'date-request-event',
    badges: getBadges(item),

    // Layout and behavior props
    isMobileView: true,
    openInNewTab: false,
  }
}

const handleClick = (item: DateRequestSimilarEvent) => {
  // Use permalink as unique identifier since object seems to be the same for all items
  if (requested) {
    return
  }

  const itemId = item.id

  selectedEventId.value = itemId
  nextTick(() => scrollDateRequestDrawerToTop())
}
</script>

<style lang="postcss" scoped>
.date-request-product-list {
  @apply w-full;
}

.date-request-product-list__container {
  @apply flex flex-col gap-2;

  &--grid {
    @apply md:grid md:grid-cols-4 md:gap-4 md:pt-1;
  }

  &--single-column {
    @apply flex flex-col;
  }
}

.date-request-product-list__item {
  @apply w-full hover:translate-y-[-2px] duration-200 ease-in-out;

  &--hidden {
    @apply md:hidden;
  }

  &--giftcard {
    @apply order-first;
  }

  &--promotion {
    @apply order-last;
  }
}

.fade-slide-up-enter-active,
.fade-slide-up-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-up-enter-from,
.fade-slide-up-leave-to {
  transform: translateY(20px);
  opacity: 0;
}

.fade-slide-up-leave-active {
  position: absolute;
  width: 100%;
}

/* Date request specific styling */
:deep(.product-card__title) {
  @apply text-xs md:text-sm pb-1 m-0;
}
:deep(.product-card) {
  @apply p-2;
}

:deep(.product-card__content) {
  @apply pt-1;
}
</style>
